// bug-207: the app-wide extension handshake status, exposed via context so any surface
// (SourcesSurface today) can gate on the *handshaken* extension without each spawning its
// own redundant handshake. The provider runs the single useExtensionBridge instance for the
// authed dashboard — it replaces the old standalone ExtensionBridgeRunner in App.tsx.
//
// "Ready" = status === 'connected' (a handshake stored a Terum token in this browser's
// extension). Merely 'detecting'/'not_installed'/'error' are NOT ready — connecting a chat
// source then would produce a source nothing can capture (the bug-207 false-CAPTURING).

import { createContext, useContext, useEffect, type ReactNode } from 'react';
import { useExtensionBridge, type ExtensionBridgeState } from './useExtensionBridge';

const ExtensionStatusContext = createContext<ExtensionBridgeState | null>(null);

export function ExtensionStatusProvider({ children }: { children: ReactNode }) {
  const bridge = useExtensionBridge();
  useEffect(() => {
    if (bridge.status === 'error') {
      console.warn('[ExtensionBridge] handshake error:', bridge.error);
    } else if (bridge.status === 'connected') {
      console.log('[ExtensionBridge] handshake connected');
    }
  }, [bridge.status, bridge.error]);
  return (
    <ExtensionStatusContext.Provider value={bridge}>{children}</ExtensionStatusContext.Provider>
  );
}

// Never throws outside the provider (unit renders / tests / a surface mounted in isolation)
// — defaults to "detecting" so callers treat the extension as not-yet-ready, not absent.
export function useExtensionStatus(): ExtensionBridgeState {
  return (
    useContext(ExtensionStatusContext) ?? {
      status: 'detecting',
      extensionId: null,
      error: null,
      retry: () => {},
    }
  );
}
